/**
 * Performance Optimization Utilities
 *
 * React performance helpers including:
 * - Memoization utilities
 * - Debouncing and throttling
 * - Lazy loading helpers
 * - Performance monitoring
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// ============================================
// Debounce and Throttle
// ============================================

/**
 * Debounce function
 * Delays function execution until after wait time has elapsed since last call
 *
 * @param {Function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Throttle function
 * Limits function execution to once per specified time period
 *
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export const throttle = (func, limit = 300) => {
  let inThrottle;

  return function executedFunction(...args) {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// ============================================
// React Hooks for Performance
// ============================================

/**
 * useDebounce Hook
 * Returns a debounced value that only updates after the specified delay
 *
 * @param {any} value - Value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {any} Debounced value
 */
export const useDebounce = (value, delay = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * useThrottle Hook
 * Returns a throttled value that updates at most once per specified interval
 *
 * @param {any} value - Value to throttle
 * @param {number} interval - Interval in milliseconds
 * @returns {any} Throttled value
 */
export const useThrottle = (value, interval = 300) => {
  const [throttledValue, setThrottledValue] = useState(value);
  const lastUpdated = useRef(Date.now());

  useEffect(() => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdated.current;

    if (timeSinceLastUpdate >= interval) {
      setThrottledValue(value);
      lastUpdated.current = now;
    } else {
      const timeoutId = setTimeout(() => {
        setThrottledValue(value);
        lastUpdated.current = Date.now();
      }, interval - timeSinceLastUpdate);

      return () => clearTimeout(timeoutId);
    }
  }, [value, interval]);

  return throttledValue;
};

/**
 * useMemoCompare Hook
 * Only updates memoized value when the comparison function returns true
 *
 * @param {any} value - Value to memoize
 * @param {Function} compare - Comparison function (prev, next) => boolean
 * @returns {any} Memoized value
 */
export const useMemoCompare = (value, compare) => {
  const previousRef = useRef();
  const previous = previousRef.current;

  const isEqual = compare(previous, value);

  useEffect(() => {
    if (!isEqual) {
      previousRef.current = value;
    }
  });

  return isEqual ? previous : value;
};

/**
 * useDeepMemo Hook
 * Deep comparison memoization (useful for objects and arrays)
 *
 * @param {Function} factory - Factory function that returns the value
 * @param {Array} deps - Dependencies array
 * @returns {any} Memoized value
 */
export const useDeepMemo = (factory, deps) => {
  const ref = useRef({ deps: undefined, value: undefined });

  const depsChanged = !ref.current.deps || !deepEqual(deps, ref.current.deps);

  if (depsChanged) {
    ref.current.deps = deps;
    ref.current.value = factory();
  }

  return ref.current.value;
};

/**
 * Deep equality check
 */
const deepEqual = (obj1, obj2) => {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
};

// ============================================
// Lazy Loading Helpers
// ============================================

/**
 * Lazy load images with intersection observer
 *
 * @param {string} src - Image source URL
 * @param {string} placeholder - Placeholder image URL
 * @returns {Object} { src: string, isLoaded: boolean, ref: RefObject }
 */
export const useLazyImage = (src, placeholder = '') => {
  const [imageSrc, setImageSrc] = useState(placeholder);
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    let observer;
    const img = imgRef.current;

    if (img && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setImageSrc(src);
              setIsLoaded(true);
              observer.unobserve(img);
            }
          });
        },
        {
          rootMargin: '50px' // Start loading 50px before visible
        }
      );

      observer.observe(img);
    } else {
      // Fallback for browsers without IntersectionObserver
      setImageSrc(src);
      setIsLoaded(true);
    }

    return () => {
      if (observer && img) {
        observer.unobserve(img);
      }
    };
  }, [src]);

  return { src: imageSrc, isLoaded, ref: imgRef };
};

/**
 * useIntersectionObserver Hook
 * Generic intersection observer hook for lazy loading
 *
 * @param {Object} options - IntersectionObserver options
 * @returns {Array} [ref, isIntersecting]
 */
export const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '0px',
        ...options
      }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [options]);

  return [targetRef, isIntersecting];
};

// ============================================
// Performance Monitoring
// ============================================

/**
 * useRenderCount Hook
 * Counts component renders (useful for debugging)
 *
 * @param {string} componentName - Name of the component
 */
export const useRenderCount = (componentName = 'Component') => {
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;
    if (process.env.NODE_ENV === 'development') {
      console.log(`${componentName} rendered ${renderCount.current} times`);
    }
  });

  return renderCount.current;
};

/**
 * useWhyDidYouUpdate Hook
 * Logs which props changed and caused a re-render
 *
 * @param {string} name - Component name
 * @param {Object} props - Component props
 */
export const useWhyDidYouUpdate = (name, props) => {
  const previousProps = useRef();

  useEffect(() => {
    if (previousProps.current && process.env.NODE_ENV === 'development') {
      const allKeys = Object.keys({ ...previousProps.current, ...props });
      const changedProps = {};

      allKeys.forEach((key) => {
        if (previousProps.current[key] !== props[key]) {
          changedProps[key] = {
            from: previousProps.current[key],
            to: props[key]
          };
        }
      });

      if (Object.keys(changedProps).length > 0) {
        console.log('[why-did-you-update]', name, changedProps);
      }
    }

    previousProps.current = props;
  });
};

/**
 * Measure component render time
 *
 * @param {string} label - Label for the measurement
 */
export const useMeasureRender = (label = 'Render') => {
  const startTime = useRef();

  useEffect(() => {
    startTime.current = performance.now();

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime.current;

      if (process.env.NODE_ENV === 'development') {
        console.log(`[${label}] Render time: ${duration.toFixed(2)}ms`);
      }
    };
  });
};

// ============================================
// Callback Optimization
// ============================================

/**
 * useStableCallback Hook
 * Creates a stable callback reference that doesn't change between renders
 * but always calls the latest version of the function
 *
 * @param {Function} callback - Callback function
 * @returns {Function} Stable callback
 */
export const useStableCallback = (callback) => {
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  return useCallback((...args) => {
    return callbackRef.current?.(...args);
  }, []);
};

/**
 * useEventCallback Hook
 * Similar to useCallback but always has access to latest props/state
 *
 * @param {Function} callback - Event callback function
 * @returns {Function} Event callback
 */
export const useEventCallback = (callback) => {
  const ref = useRef();

  useEffect(() => {
    ref.current = callback;
  });

  return useCallback((...args) => ref.current?.(...args), []);
};

// ============================================
// Memoization Helpers
// ============================================

/**
 * Memoize expensive calculations
 *
 * @param {Function} fn - Function to memoize
 * @param {Function} keyGen - Key generator function
 * @returns {Function} Memoized function
 */
export const memoize = (fn, keyGen = (...args) => JSON.stringify(args)) => {
  const cache = new Map();

  return (...args) => {
    const key = keyGen(...args);

    if (cache.has(key)) {
      return cache.get(key);
    }

    const result = fn(...args);
    cache.set(key, result);

    return result;
  };
};

/**
 * Memoize with LRU cache (Least Recently Used)
 *
 * @param {Function} fn - Function to memoize
 * @param {number} maxSize - Maximum cache size
 * @returns {Function} Memoized function
 */
export const memoizeLRU = (fn, maxSize = 100) => {
  const cache = new Map();

  return (...args) => {
    const key = JSON.stringify(args);

    if (cache.has(key)) {
      // Move to end (most recently used)
      const value = cache.get(key);
      cache.delete(key);
      cache.set(key, value);
      return value;
    }

    const result = fn(...args);

    // Add new entry
    cache.set(key, result);

    // Remove oldest if exceeds max size
    if (cache.size > maxSize) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    return result;
  };
};

// ============================================
// Virtual Scrolling Helper
// ============================================

/**
 * useVirtualScroll Hook
 * Implements virtual scrolling for large lists
 *
 * @param {Array} items - All items in the list
 * @param {number} itemHeight - Height of each item in pixels
 * @param {number} containerHeight - Height of the scroll container
 * @returns {Object} Virtual scroll data
 */
export const useVirtualScroll = (items = [], itemHeight = 50, containerHeight = 500) => {
  const [scrollTop, setScrollTop] = useState(0);

  const totalHeight = items.length * itemHeight;
  const startIndex = Math.floor(scrollTop / itemHeight);
  const endIndex = Math.min(
    startIndex + Math.ceil(containerHeight / itemHeight) + 1,
    items.length
  );

  const visibleItems = items.slice(startIndex, endIndex);
  const offsetY = startIndex * itemHeight;

  const handleScroll = useCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);

  return {
    visibleItems,
    totalHeight,
    offsetY,
    handleScroll,
    startIndex,
    endIndex
  };
};

// ============================================
// Export All
// ============================================

const performanceUtils = {
  // Debounce & Throttle
  debounce,
  throttle,
  useDebounce,
  useThrottle,

  // Memoization
  useMemoCompare,
  useDeepMemo,
  memoize,
  memoizeLRU,

  // Lazy Loading
  useLazyImage,
  useIntersectionObserver,

  // Performance Monitoring
  useRenderCount,
  useWhyDidYouUpdate,
  useMeasureRender,

  // Callbacks
  useStableCallback,
  useEventCallback,

  // Virtual Scrolling
  useVirtualScroll
};
export default performanceUtils;
